import { useLayoutEffect, useRef } from "react";

import { FinancialAccountFragment } from "@/__generated__/graphql";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { useScope } from "@/hooks/useScope";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { Objects } from "@/objects";
import { State } from "@/state";

export const COMMAND_ID = "select-financial-account";

export type SelectFinancialAccountContextType = {
  /** Callback fired when the user picks an account from the list. */
  onSubmit?: (account: FinancialAccountFragment) => void;
  /** Optional entity scoping; defaults to ``useScope().activeEntityIdentifiers``. */
  entityIdentifiers?: string[];
  /** Custom list filter; overrides the default "bank-only" predicate. */
  filterOptions?: (
    options: FinancialAccountFragment[],
    filter: string,
  ) => FinancialAccountFragment[];
  /**
   * When true (the default), only ``FinancialAccountObject`` rows are
   * shown (excluding Stripe union members). Set to ``false`` to bypass.
   */
  bankAccountsOnly?: boolean;
};

function Component({
  context,
}: {
  commandInstanceId: string;
  commandDefinitionId: string;
  context?: SelectFinancialAccountContextType;
}) {
  const activeIndexRef = useRef<{
    data: FinancialAccountFragment | null;
    onClick: () => void;
  }>({ data: null, onClick: () => {} });
  const { activeEntityIdentifiers } = useScope();
  const entityIdentifiers =
    context?.entityIdentifiers ?? activeEntityIdentifiers;

  // Keep this predicate aligned with ``FinancialAccountObject.getActions``
  // — the per-card "Upload transactions" button uses the same gate, so the
  // subbar entry point and the card entry point can never disagree
  // about which accounts are eligible. Stripe accounts surface as
  // ``__typename === "StripeFinancialAccountObject"`` (different union member,
  // different object registration).
  const defaultFilter = (
    options: FinancialAccountFragment[],
  ): FinancialAccountFragment[] =>
    options.filter((option) => option?.__typename === "FinancialAccountObject");

  return (
    <StickyLayout
      fillHeight={false}
      stickyBottomChildren={
        <CommandCenterListFooter
          buttons={[
            {
              variant: "default",
              size: "sm",
              children: "Submit",

              onClick: () => {
                activeIndexRef.current.onClick?.();
              },

              hotkey: "enter",
            },
          ]}
        />
      }
    >
      <NodeList
        {...Objects.FinancialAccountObject.getReferenceValueOptions(
          entityIdentifiers,
        )}
        fillHeight={false}
        onSelectOption={(account: FinancialAccountFragment) => {
          context?.onSubmit?.(account);
        }}
        filterOptions={
          context?.filterOptions ??
          (context?.bankAccountsOnly === false ? undefined : defaultFilter)
        }
        onActiveIndexChange={(data: FinancialAccountFragment, onClick) => {
          activeIndexRef.current.data = data;
          activeIndexRef.current.onClick = onClick;
        }}
        className="max-w-none"
      />
    </StickyLayout>
  );
}

export function SelectFinancialAccountCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Select financial account",
      description: "Select a bank account…",
      getLabel: () => "Select financial account",
      component: Component,
      isHidden: true,
    });

    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
